import { useEffect, useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import Input from "../../form/input/InputField";
import Checkbox from "../../form/input/Checkbox";
import MultiSelectCheckbox from '../../form/MultiSelectCheckbox';
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import Alert from "../../ui/alert/Alert";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { authService } from "../../../services/authService";
import api from "../../../services/api";

const UTMIFY_API_URL = "https://api.utmify.com.br/api-credentials/orders";

type UtmifyEvent =
    | 'sale_approved'
    | 'sale_rejected'
    | 'sale_refunded'
    | 'sale_chargeback'
    | 'sale_abandoned'
    | 'sale_pix_generated'
    | 'sale_boleto_generated';

const SALE_EVENTS: { id: UtmifyEvent; label: string }[] = [
    { id: 'sale_approved', label: 'Compra Aprovada' },
    { id: 'sale_rejected', label: 'Compra Recusada' },
    { id: 'sale_refunded', label: 'Reembolso' },
    { id: 'sale_chargeback', label: 'Chargeback' },
    { id: 'sale_pix_generated', label: 'Pix Gerado' },
    { id: 'sale_boleto_generated', label: 'Boleto Gerado' },
];

const ALL_EVENT_IDS = SALE_EVENTS.map((e) => e.id);

interface UtmifyFormData {
    active: boolean;
    name: string;
    accessToken: string;
    productList: string[];
    offersList: string[];
    events: UtmifyEvent[];
}

interface UtmifyIntegrationRaw {
    id: string;
    name?: string | null;
    accessToken: string | null;
    productList?: string[] | null;
    offersList?: string[] | null;
    events?: UtmifyEvent[] | null;
    active?: boolean;
}

interface ProductApi {
    id: string;
    name: string;
}

interface OfferApi {
    id: string;
    name: string;
}

interface UtmifyIntegrationUpdatePayload {
    status: 'active' | 'inactive';
    apiUrl: string;
    accessToken: string;
    events: UtmifyEvent[];
    productList: string[];
    offersList: string[];
    name?: string;
}

interface EditUtmifyFormProps {
    integrationId?: string;
    onSuccess?: (data: unknown) => void;
    onError?: (error: unknown) => void;
}

export default function EditUtmifyForm({ integrationId, onSuccess, onError }: EditUtmifyFormProps) {
    const navigate = useNavigate();
    const params = useParams();
    const currentId = integrationId || params.integrationId;
    const companyId = authService.getCompanyId();

    const [formData, setFormData] = useState<UtmifyFormData>({
        name: "",
        accessToken: "",
        productList: [],
        offersList: [],
        events: [...ALL_EVENT_IDS],
        active: true,
    });

    const [loading, setLoading] = useState(false);
    const [loadingIntegration, setLoadingIntegration] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [selectAllEvents, setSelectAllEvents] = useState(true);

    const [products, setProducts] = useState<Array<{ value: string; label: string }>>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectAllProducts, setSelectAllProducts] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

    const [offers, setOffers] = useState<Array<{ value: string; label: string }>>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);

    const [otherTokens, setOtherTokens] = useState<string[]>([]);

    useEffect(() => {
        fetchProducts();
        if (currentId) {
            fetchIntegration(currentId);
            fetchOtherTokens(currentId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentId]);

    useEffect(() => {
        setSelectAllEvents(formData.events.length === SALE_EVENTS.length);
    }, [formData.events.length]);

    useEffect(() => {
        if (loadingIntegration) return;
        if (selectAllProducts) {
            setOffers([]);
            setSelectedOffers([]);
            setSelectAllOffers(false);
            setFormData((prev) => ({ ...prev, offersList: [] }));
            return;
        }
        if (formData.productList.length === 0) {
            setOffers([]);
            return;
        }
        fetchOffersForProducts(formData.productList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.productList.join(","), selectAllProducts, loadingIntegration]);

    const fetchIntegration = async (id: string) => {
        try {
            setLoadingIntegration(true);
            const response = await api.get(`/api/integrations/${companyId}/by-id/${id}`);

            const raw = response.data?.data as UtmifyIntegrationRaw | undefined;
            if (!raw) {
                setApiError("Integração não encontrada.");
                return;
            }

            const loadedProducts: string[] = raw.productList || [];
            const loadedOffers: string[] = raw.offersList || [];
            const isAllProducts = loadedProducts.length === 0;
            const isAllOffers = loadedOffers.length === 0;

            setFormData({
                name: raw.name || "",
                accessToken: raw.accessToken || "",
                productList: loadedProducts,
                offersList: loadedOffers,
                // "Abandono" não existe na Utmify: some das integrações salvas ao editar
                events: ((raw.events as UtmifyEvent[]) || []).filter((e) => e !== 'sale_abandoned'),
                active: raw.active !== false,
            });

            setSelectAllProducts(isAllProducts);
            setSelectedProducts(isAllProducts ? ['all'] : loadedProducts);

            setSelectAllOffers(!isAllProducts && isAllOffers);
            setSelectedOffers(!isAllProducts && isAllOffers ? ['all'] : loadedOffers);
        } catch (error: unknown) {
            console.error("Erro ao carregar integração:", error);
            const message = axios.isAxiosError(error)
                ? error.response?.data?.error || error.message
                : error instanceof Error
                    ? error.message
                    : "Erro ao carregar integração";
            setApiError(message || "Erro ao carregar integração");
        } finally {
            setLoadingIntegration(false);
        }
    };

    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products?status=active`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );

            if (response.data && response.data.products) {
                setProducts((response.data.products as ProductApi[]).map((p) => ({
                    value: p.id,
                    label: p.name,
                })));
            }
        } catch (error) {
            console.error("Erro ao carregar produtos:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const fetchOffersForProducts = async (productIds: string[]) => {
        try {
            setLoadingOffers(true);
            const results = await Promise.all(
                productIds.map((pid) =>
                    axios.get(
                        `${import.meta.env.VITE_API_URL}/api/products/${pid}/offers-with-links`,
                        {
                            headers: {
                                'Authorization': `Bearer ${authService.getToken()}`,
                            },
                        }
                    ).then((resp) => {
                        if (resp.data && Array.isArray(resp.data.offers)) {
                            return (resp.data.offers as OfferApi[]).map((o) => ({
                                value: o.id,
                                label: o.name,
                            }));
                        }
                        return [] as Array<{ value: string; label: string }>;
                    }).catch(() => [] as Array<{ value: string; label: string }>)
                )
            );

            const merged: Array<{ value: string; label: string }> = [];
            const seen = new Set<string>();
            for (const list of results) {
                for (const offer of list) {
                    if (!seen.has(offer.value)) {
                        seen.add(offer.value);
                        merged.push(offer);
                    }
                }
            }
            setOffers(merged);

            setSelectedOffers((prev) => prev.filter((id) => id === 'all' || seen.has(id)));
            setFormData((prev) => ({
                ...prev,
                offersList: prev.offersList.filter((id) => seen.has(id)),
            }));
        } catch (error) {
            console.error("Erro ao buscar ofertas dos produtos:", error);
            setOffers([]);
        } finally {
            setLoadingOffers(false);
        }
    };

    const fetchOtherTokens = async (currentIntegrationId: string) => {
        try {
            const response = await api.get(`/api/integrations/${companyId}/list/utmify`);
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                const tokens = (response.data.data as UtmifyIntegrationRaw[])
                    .filter((i) => i.id !== currentIntegrationId)
                    .map((i) => i.accessToken)
                    .filter((t): t is string => !!t);
                setOtherTokens(tokens);
            }
        } catch (error) {
            console.error("Erro ao buscar tokens existentes:", error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setErrors((prev) => ({ ...prev, [id]: "" }));
        setApiError("");
        setApiSuccess("");
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [id]: checked }));
    };

    const handleEventChange = (event: UtmifyEvent, checked: boolean) => {
        setErrors((prev) => ({ ...prev, events: "" }));
        setApiError("");
        setApiSuccess("");

        setFormData((prev) => ({
            ...prev,
            events: checked
                ? [...prev.events, event]
                : prev.events.filter((e) => e !== event),
        }));
    };

    const toggleSelectAllEvents = () => {
        setErrors((prev) => ({ ...prev, events: "" }));
        setFormData((prev) => ({
            ...prev,
            events: selectAllEvents ? [] : [...ALL_EVENT_IDS],
        }));
        setSelectAllEvents(!selectAllEvents);
    };

    const handleProductsChange = (values: string[]) => {
        const isAllSelected = values.includes('all');
        setErrors((prev) => ({ ...prev, productList: "" }));
        setApiError("");
        setApiSuccess("");

        if (isAllSelected) {
            setSelectAllProducts(true);
            setSelectedProducts(['all']);
            setFormData((prev) => ({ ...prev, productList: [] }));
        } else {
            setSelectAllProducts(false);
            setSelectedProducts(values);
            setFormData((prev) => ({ ...prev, productList: values }));
        }
    };

    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');
        setErrors((prev) => ({ ...prev, offersList: "" }));
        setApiError("");
        setApiSuccess("");

        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormData((prev) => ({ ...prev, offersList: [] }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormData((prev) => ({ ...prev, offersList: values }));
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.accessToken.trim()) {
            newErrors.accessToken = "Chave API é obrigatória";
        } else if (otherTokens.includes(formData.accessToken.trim())) {
            newErrors.accessToken = "Já existe outra integração UTMify com essa chave de API.";
        }

        if (!formData.events.length) {
            newErrors.events = "Pelo menos um evento deve ser selecionado";
        }

        if (!selectAllProducts && formData.productList.length === 0) {
            newErrors.productList = "Selecione 'Todos os produtos' ou ao menos um produto";
        }

        if (!selectAllProducts && formData.productList.length > 0) {
            if (!selectAllOffers && formData.offersList.length === 0) {
                newErrors.offersList = "Selecione 'Todas as ofertas' ou ao menos uma oferta";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm() || !currentId) return;

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            const payload: UtmifyIntegrationUpdatePayload = {
                status: formData.active ? 'active' : 'inactive',
                apiUrl: UTMIFY_API_URL,
                accessToken: formData.accessToken.trim(),
                events: formData.events,
                productList: selectAllProducts ? [] : formData.productList,
                offersList: selectAllProducts || selectAllOffers ? [] : formData.offersList,
                name: formData.name.trim() || undefined,
            };

            const response = await api.put(
                `/api/integrations/${companyId}/by-id/${currentId}`,
                payload
            );

            if (response.data && response.data.success) {
                setApiSuccess("Integração atualizada com sucesso!");
                if (onSuccess) onSuccess(response.data.data);
                setTimeout(() => navigate('/apps/utmify'), 1500);
            } else {
                setApiError(response.data?.error || "Erro ao atualizar integração");
                if (onError) onError(response.data?.error);
            }
        } catch (error: unknown) {
            console.error("Erro ao atualizar integração UTMify:", error);
            const message = axios.isAxiosError(error)
                ? error.response?.data?.error || error.message
                : error instanceof Error
                    ? error.message
                    : "Erro ao atualizar integração";
            setApiError(message || "Erro ao atualizar integração");
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate(`/apps/utmify`);
    };

    const showOffersSection = !selectAllProducts && formData.productList.length > 0;

    if (loadingIntegration) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
                <span className="ml-3 dark:text-white/90">Carregando integração...</span>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Editar integração UTMify">
                    {apiError && (
                        <div className="mt-4">
                            <Alert
                                variant="error"
                                title="Erro"
                                message={apiError}
                            />
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="mt-4">
                            <Alert
                                variant="success"
                                title="Sucesso"
                                message={apiSuccess}
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-4 mb-6">
                        <img
                            src="/images/integration/utmify-icon.png"
                            alt="Utmify"
                            className="w-10 h-10 object-contain"
                        />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Utmify
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Edite os dados da integração para ajustar produtos, ofertas e eventos.
                            </p>
                        </div>
                    </div>

                    <hr className="border-gray-200 dark:border-gray-700 mb-6" />

                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar integração"
                                checked={formData.active}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome da integração</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="accessToken">Chave API *</Label>
                            <Input
                                type="text"
                                id="accessToken"
                                placeholder="Informe sua chave API"
                                value={formData.accessToken}
                                onChange={handleInputChange}
                                error={!!errors.accessToken}
                                hint={errors.accessToken}
                            />
                        </div>

                        <div className="mt-5">
                            <Label>Produtos *</Label>
                            {loadingProducts ? (
                                <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                    Carregando produtos...
                                </div>
                            ) : (
                                <div className="mt-2">
                                    <MultiSelectCheckbox
                                        options={[
                                            {
                                                id: 'all',
                                                label: 'Todos os produtos',
                                                disabled: false,
                                            },
                                            ...products.filter((product) => product.value !== 'any').map((product) => ({
                                                id: product.value,
                                                label: `${product.label}`,
                                                disabled: false,
                                                meta: product,
                                            })),
                                        ]}
                                        selectedValues={selectAllProducts ? ['all'] : selectedProducts}
                                        onChange={handleProductsChange}
                                        placeholder="Selecione os produtos"
                                        error={!!errors.productList}
                                        hint={errors.productList}
                                        searchPlaceholder="Pesquisar produtos"
                                        showSelectAll={false}
                                    />
                                    {selectAllProducts && (
                                        <p className="mt-1 text-xs text-blue-500">
                                            Esta integração será válida para todos os produtos, incluindo os futuros.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {showOffersSection && (
                            <div className="mt-5">
                                <Label>Ofertas *</Label>
                                {loadingOffers ? (
                                    <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                        Carregando ofertas...
                                    </div>
                                ) : (
                                    <div className="mt-2">
                                        <MultiSelectCheckbox
                                            options={[
                                                {
                                                    id: 'all',
                                                    label: 'Todas as ofertas',
                                                    disabled: false,
                                                },
                                                ...offers.map((offer) => ({
                                                    id: offer.value,
                                                    label: offer.label,
                                                    disabled: false,
                                                    meta: offer,
                                                })),
                                            ]}
                                            selectedValues={selectAllOffers ? ['all'] : selectedOffers}
                                            onChange={handleOffersChange}
                                            placeholder="Selecione as ofertas"
                                            error={!!errors.offersList}
                                            hint={errors.offersList}
                                            searchPlaceholder="Pesquisar ofertas"
                                            showSelectAll={false}
                                        />
                                        {selectAllOffers && (
                                            <p className="mt-1 text-xs text-blue-500">
                                                Esta integração será válida para todas as ofertas dos produtos selecionados, incluindo as futuras.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-5">
                            <div className="flex flex-row items-center gap-4 mt-6">
                                <Label>Selecione os eventos: *</Label>
                                <span
                                    className="text-sm text-blue-500 underline cursor-pointer"
                                    onClick={toggleSelectAllEvents}
                                >
                                    {selectAllEvents ? '(Desmarcar todos)' : '(Selecionar todos)'}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 mb-2">
                                <div className="space-y-3">
                                    <Label className="mb-3">Eventos de Venda</Label>
                                    {SALE_EVENTS.map((event) => (
                                        <Checkbox
                                            key={event.id}
                                            id={event.id}
                                            checked={formData.events.includes(event.id)}
                                            onChange={(checked) => handleEventChange(event.id, checked)}
                                            label={event.label}
                                        />
                                    ))}
                                </div>
                            </div>
                            {errors.events && (
                                <p className="text-red-600 text-xs mt-1">{errors.events}</p>
                            )}
                        </div>

                        <div className="mt-8 flex justify-end gap-4.5">
                            <Button
                                size="md"
                                variant="outline"
                                onClick={handleGoBack}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="md"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : "Salvar alterações"}
                            </Button>
                        </div>
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}
